/**
 * 파일 보관함 카테고리 메타데이터.
 *
 * 카테고리별 아이콘, 한글명, 설명, 강조 색상을 정의한다.
 * 카테고리 식별자 자체는 기존(`map | cad | csv | roi | learning | docs | nvr`)와 동일하게 유지.
 */

import React from 'react';
import {
  Map as MapIcon,
  Architecture as CadIcon,
  TableChart as CsvIcon,
  Crop as RoiIcon,
  Psychology as LearningIcon,
  Description as DocsIcon,
  Videocam as NvrIcon,
} from '@mui/icons-material';
import type { FileCategory } from '../../models/FileStorage';

export interface CategoryMeta {
  id: FileCategory;
  /** 한글 이름 (사용자 표시용). */
  label: string;
  /** 영문 코드 (기존 호환을 위해 함께 표시). */
  code: string;
  /** 1줄 설명 — 사용자가 무엇을 보관해야 하는지 한눈에 알 수 있도록. */
  description: string;
  /** MUI 아이콘 컴포넌트. */
  Icon: React.ComponentType<{ fontSize?: 'inherit' | 'small' | 'medium' | 'large' }>;
  /** 강조 색상 (16진). 카드/아이콘/활성 상태 표시에 사용. */
  accent: string;
}

export const CATEGORY_META: CategoryMeta[] = [
  {
    id: 'map',
    label: '주차 맵',
    code: 'Map',
    description: '맵 에디터 JSON · 자동 저장 / 버전 관리',
    Icon: MapIcon,
    accent: '#3b82f6',
  },
  {
    id: 'cad',
    label: '도면 (CAD)',
    code: 'CAD',
    description: 'DXF 설치 도면 · 레이아웃 원본',
    Icon: CadIcon,
    accent: '#06b6d4',
  },
  {
    id: 'csv',
    label: '객체 데이터',
    code: 'CSV',
    description: 'DXF 변환 결과 · 객체 좌표 CSV',
    Icon: CsvIcon,
    accent: '#10b981',
  },
  {
    id: 'roi',
    label: 'ROI 정의',
    code: 'ROI',
    description: '관심 영역 (주차면) 매핑 파일',
    Icon: RoiIcon,
    accent: '#f59e0b',
  },
  {
    id: 'learning',
    label: '학습 데이터',
    code: 'Learning',
    description: '학습용 이미지 · CCTV별 폴더',
    Icon: LearningIcon,
    accent: '#8b5cf6',
  },
  {
    id: 'docs',
    label: '문서/기타',
    code: 'Docs',
    description: '메모, PDF, 매뉴얼 등 기타 파일',
    Icon: DocsIcon,
    accent: '#64748b',
  },
  {
    id: 'nvr',
    label: 'NVR 녹화',
    code: 'NVR',
    description: 'NVR 녹화 영상 / 내보내기 파일',
    Icon: NvrIcon,
    accent: '#ef4444',
  },
];

/** 카테고리 ID → 메타 lookup helper. */
export function getCategoryMeta(id: FileCategory): CategoryMeta {
  return (
    CATEGORY_META.find((c) => c.id === id) ??
    // `test` 등 UI 미노출 카테고리 폴백 — Docs 메타로 대체.
    (CATEGORY_META.find((c) => c.id === 'docs') as CategoryMeta)
  );
}
